import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 10000-10999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const schedulesErrors = {
  SCHEDULE_MISSING: {
    errorCode: 10002,
    errorMessage: t("Schedule does not exist"),
    isDynamic: false,
  },
  SCHEDULE_IMPORT_FILE: {
    errorCode: 10011,
    errorMessage: t("The schedule file could not be imported"),
    isDynamic: false,
  },
  // `VERSION_IN_PROGRESS_MSG` ("Another heavy operation is in progress. Please try again later.") is
  // a shared concurrency guard reused verbatim across schedule/scheduleVersions/calendar/resources —
  // every one of those call sites paired it with the generic SOMETHING_WENT_WRONG; this is the one
  // shared code for all of them, not a per-file duplicate.
  SCHEDULE_VERSION_OPERATION_IN_PROGRESS: {
    errorCode: 10013,
    errorMessage: t("Another heavy operation is in progress. Please try again later."),
    isDynamic: false,
  },
  // scheduleMongo.service.ts `batchUpdateProgress`: the schedule's current version could not be
  // resolved (no `latestVersionId`) — distinct from SCHEDULE_MISSING (the schedule itself, not a
  // version of it).
  SCHEDULE_VERSION_NOT_FOUND: {
    errorCode: 10014,
    errorMessage: t("Schedule version not found"),
    isDynamic: false,
  },
  // scheduleMongo.service.ts `deleteTaskType`: one or more of the selected task types are still
  // assigned to live schedule activities, so they can't be deleted.
  SCHEDULE_TASK_TYPE_IN_USE: {
    errorCode: 10015,
    errorMessage: t("These task types are still assigned to schedule activities and cannot be deleted"),
    isDynamic: false,
  },
  // scheduleMongo.service.ts `deleteAppearance`: one or more of the selected appearances are still
  // assigned to a task type, so they can't be deleted. Same shape of rule as
  // SCHEDULE_TASK_TYPE_IN_USE, different resource.
  SCHEDULE_APPEARANCE_IN_USE: {
    errorCode: 10016,
    errorMessage: t("One or more appearances are still assigned to a task type and were not deleted"),
    isDynamic: false,
  },
  // 10017-10036 is the schedule module's SOMETHING_WENT_WRONG (9000) / RESOURCE_NOT_FOUND (9003)
  // split. Two sites needed no new code — SCHEDULE_VERSION_NOT_FOUND (10014) already carried
  // "Schedule version not found" verbatim, and four "Could not resolve the current user." sites went
  // to the shared CURRENT_USER_NOT_RESOLVED (7087). Nineteen sites stay generic: most forward a
  // caught `error.message`, the rest literally say "Something went wrong", and one interpolates
  // MAX_TASK_FILES so it keeps the standing dynamic-message deferral.
  //
  // Deliberately NOT folded into SCHEDULE_IMPORT_FILE (10011): that code's sentence is
  // "The schedule file could not be imported", this site's is its own.
  // 10017-10036 — the schedule module's split. Messages copied verbatim from the call sites.
  SCHEDULE_IMPORT_FILE_UNEXPECTED: {
    errorCode: 10017,
    errorMessage: t("Something went wrong while importing the schedule file."),
    isDynamic: false,
  },
  // 3 call sites; one writes the sentence without its trailing period (non-material).
  SCHEDULE_EXPORT_QUEUE_FAILED: {
    errorCode: 10018,
    errorMessage: t("The export could not be queued."),
    isDynamic: false,
  },
  SCHEDULE_ACTIVITY_FIELDS_REQUIRED: {
    errorCode: 10020,
    errorMessage: t("text, params.spaceId and progress are required to save a schedule activity."),
    isDynamic: false,
  },
  // Distinct from SCHEDULE_APPEARANCE_IN_USE (10016): these appearances are gone, not in use.
  SCHEDULE_APPEARANCES_NOT_FOUND: {
    errorCode: 10021,
    errorMessage: t("One or more of the selected appearances no longer exists."),
    isDynamic: false,
  },
  SCHEDULE_TASK_TYPE_SAVE_FAILED: {
    errorCode: 10022,
    errorMessage: t("The task type could not be saved — the update matched no row and no insert was acknowledged."),
    isDynamic: false,
  },
  SCHEDULE_TASK_TYPES_NOT_IN_SCHEDULE: {
    errorCode: 10023,
    errorMessage: t("None of the given task types belong to this Schedule, so nothing was deleted."),
    isDynamic: false,
  },
  SCHEDULE_NO_APPEARANCES: {
    errorCode: 10024,
    errorMessage: t("No appearances found for this schedule."),
    isDynamic: false,
  },
  SCHEDULE_APPEARANCE_UPDATE_FAILED: {
    errorCode: 10025,
    errorMessage: t("The appearance could not be updated."),
    isDynamic: false,
  },
  SCHEDULE_APPEARANCE_CREATE_FAILED: {
    errorCode: 10026,
    errorMessage: t("The appearance could not be created."),
    isDynamic: false,
  },
  // The two guards are separate: the parent row is missing, versus present but with no record
  // status. Each ships its own sentence.
  SCHEDULE_PARENT_SPACE_OR_PROJECT_NOT_FOUND: {
    errorCode: 10028,
    errorMessage: t("The Space or Project this schedule belongs to no longer exists."),
    isDynamic: false,
  },
  SCHEDULE_PARENT_SPACE_OR_PROJECT_NO_RECORD_STATUS: {
    errorCode: 10029,
    errorMessage: t("The Space or Project this schedule belongs to has no record status."),
    isDynamic: false,
  },
  SCHEDULE_INSERT_NOT_ACKNOWLEDGED: {
    errorCode: 10030,
    errorMessage: t("The schedule insert was not acknowledged."),
    isDynamic: false,
  },
  // `updateTask`'s scheduleId branch answered `{ status: update.acknowledged }` — a computed boolean
  // with no reason attached. The refusal half now names this, the update-side twin of 10030.
  SCHEDULE_ACTIVITY_UPDATE_NOT_ACKNOWLEDGED: {
    errorCode: 10055,
    errorMessage: t("The schedule activity update was not acknowledged."),
    isDynamic: false,
  },
  // A partial success: the schedule exists, its default task type does not.
  SCHEDULE_DEFAULT_TASK_TYPE_SETUP_FAILED: {
    errorCode: 10031,
    errorMessage: t("The schedule was created but its default task type could not be set up."),
    isDynamic: false,
  },
  // Deliberately not SCHEDULE_MISSING (10002), whose sentence is "Schedule does not exist".
  SCHEDULE_NOT_FOUND_PLAIN: {
    errorCode: 10032,
    errorMessage: t("Schedule not found"),
    isDynamic: false,
  },
  SCHEDULE_DELETE_FAILED: {
    errorCode: 10033,
    errorMessage: t("Delete operation failed"),
    isDynamic: false,
  },
  SCHEDULE_FILE_ATTACH_FAILED: {
    errorCode: 10034,
    errorMessage: t("The file could not be attached to the schedule's current version."),
    isDynamic: false,
  },
  SCHEDULE_EXPORT_ROW_NOT_FOUND: {
    errorCode: 10035,
    errorMessage: t("No export row matched that id in this portal."),
    isDynamic: false,
  },
  SCHEDULE_EXPORT_ENTRY_DELETE_FAILED: {
    errorCode: 10036,
    errorMessage: t("The export entry could not be deleted."),
    isDynamic: false,
  },
  // 10010 had no mapper entry until now — its call site was already correct, but the code
  // resolved to a blank message. Sentence taken from that site.
  SCHEDULE_SET_AS_TOP_VERSION: {
    errorCode: 10010,
    errorMessage: t("Failed to set as top version"),
    isDynamic: false,
  },
  // 10037-10041 is the scheduleVersions module's split, continuing this same schedules family.
  // Ten of its fifteen sites needed no new code: seven reused SCHEDULE_MISSING (10002) and one each
  // SCHEDULE_VERSION_NOT_FOUND (10014), SCHEDULE_NOT_FOUND_PLAIN (10032) and the module's own
  // SCHEDULE_SET_AS_TOP_VERSION (10010), which was already correct at its call site but had no
  // mapper entry until now.
  // 10037-10041 — the scheduleVersions module's split.
  SCHEDULE_VERSION_NAME_FAILED: {
    errorCode: 10037,
    errorMessage: t("Could not name this Schedule version."),
    isDynamic: false,
  },
  SCHEDULE_VERSION_NOTE_ADD_FAILED: {
    errorCode: 10038,
    errorMessage: t("Could not add the note to this Schedule version."),
    isDynamic: false,
  },
  SCHEDULE_VERSION_NOTE_NOT_FOUND: {
    errorCode: 10039,
    errorMessage: t("Note not found on this Schedule version."),
    isDynamic: false,
  },
  SCHEDULE_NO_CURRENT_VERSION: {
    errorCode: 10040,
    errorMessage: t("This schedule has no current version!"),
    isDynamic: false,
  },
  // 10041 (SCHEDULE_NO_CURRENT_VERSION_YET) is retired: nothing sends it. Do not reuse the number.
  // Off INSUFFICIENT_PERMISSION (7012): a cross-schedule ownership check, not an access decision.
  SCHEDULE_TASK_TYPE_NOT_IN_SCHEDULE: {
    errorCode: 10042,
    errorMessage: t("This task type does not belong to the given Schedule."),
    isDynamic: false,
  },
  SCHEDULE_NOTHING_CHANGED: {
    errorCode: 10043,
    errorMessage: t("Nothing was changed — the schedule and its current version already hold these values."),
    isDynamic: false,
  },
  // 10044-10048 came off VALIDATION_FAILED (9004).
  SCHEDULE_EXPORT_SOURCE_REQUIRED: {
    errorCode: 10044,
    errorMessage: t("Send either an awsKey (the S3 object to export) or the _id of an existing export to retry."),
    isDynamic: false,
  },
  SCHEDULE_TASK_PROJECT_ID_REQUIRED: {
    errorCode: 10045,
    errorMessage: t("projectId is required to create a task."),
    isDynamic: false,
  },
  SCHEDULE_ID_AND_SESSION_REQUIRED: {
    errorCode: 10046,
    errorMessage: t("A scheduleId and an authenticated session are required."),
    isDynamic: false,
  },
  SCHEDULE_TASK_PARENT_REQUIRED: {
    errorCode: 10047,
    errorMessage: t("data.parent is required — the task's new parent id."),
    isDynamic: false,
  },
  SCHEDULE_EXPORT_AWS_KEY_REQUIRED: {
    errorCode: 10048,
    errorMessage: t("awsKey is required to queue an export."),
    isDynamic: false,
  },
  SCHEDULE_MISSING_TASK: {
    errorCode: 10003,
    errorMessage: t("The task was not found in the current schedule version."),
    isDynamic: false,
  },
  SCHEDULE_MISSING_ID: {
    errorCode: 10001,
    errorMessage: t("scheduleId is required"),
    isDynamic: false,
  },
  SCHEDULE_MISSING_NAMED_VERSION: {
    errorCode: 10006,
    errorMessage: t("A named version already exists! Make changes to create new one."),
    isDynamic: false,
  },
  SCHEDULE_MISSING_SOURCE_VERSION: {
    errorCode: 10007,
    errorMessage: t("Source version not found!"),
    isDynamic: false,
  },
  SCHEDULE_VERSION_ALREADY_CURRENT: {
    errorCode: 10008,
    errorMessage: t("This version is already the current version!"),
    isDynamic: false,
  },
  SCHEDULE_VERSION_FAILED_CREATE_NEW: {
    errorCode: 10009,
    errorMessage: t("Failed to create new version"),
    isDynamic: false,
  },
  // Splitting SCHEDULE_MISSING_TASK and SCHEDULE_IMPORT_FILE, both umbrella codes.
  SCHEDULE_TASK_REQUIRED: {
    errorCode: 10049,
    errorMessage: t("Task is required"),
    isDynamic: false,
  },
  SCHEDULE_TASK_OR_PARENT_NOT_FOUND: {
    errorCode: 10050,
    errorMessage: t("The task or its new parent no longer exists."),
    isDynamic: false,
  },
  SCHEDULE_IMPORT_SCHEDULE_ID_INVALID: {
    errorCode: 10051,
    errorMessage: t("Invalid or missing scheduleId. Must be a 24-character hex ObjectId."),
    isDynamic: false,
  },
  SCHEDULE_IMPORT_TYPE_MISSING: {
    errorCode: 10052,
    errorMessage: t("Missing import type."),
    isDynamic: false,
  },
  // The two schedule task-link guards used to interpolate raw ObjectIds into the user-facing
  // sentence — diagnostics, not copy. The ids stay in logs; the message does not carry them.
  SCHEDULE_MISSING_SOURCE_TASK: {
    errorCode: 10004,
    errorMessage: t("Source task not found in the current schedule version."),
    isDynamic: false,
  },
  SCHEDULE_MISSING_TARGET_TASK: {
    errorCode: 10005,
    errorMessage: t("Target task not found in the current schedule version."),
    isDynamic: false,
  },
  SCHEDULE_NO_ACCESS: {
    errorCode: 10012,
    errorMessage: t("You do not have Required permission"),
    isDynamic: false,
  },
  // ⚠️ Both of these were minted on numbers that were already taken, and a duplicate VALUE is
  // something TypeScript cannot flag (only duplicate KEYS error), so the clash surfaced as the
  // client resolving the OTHER name's sentence. Renumbered 2026-09-16:
  //   10013 -> 10054: 10013 belongs to SCHEDULE_VERSION_OPERATION_IN_PROGRESS (errorCodes/schedules.ts),
  //                   which 21 call sites send; this one has a single sender, so this one moved.
  //    9008 -> 9011 : 9008 belongs to staging's CSRF block (CSRF_TOKEN_INVALID). That block is
  //                   documented in accounts/CLAUDE.md and lives on staging, so ours moved.
  // Check the number is free before adding one here — `Object.values(errorCodes)` must stay unique.
  SCHEDULE_PROGRESS_SUMMARY_ACTIVITY: {
    errorCode: 10054,
    errorMessage: t(
      "Progress cannot be updated on summary activities. It is automatically calculated from child activities.",
    ),
    isDynamic: false,
  },
  SCHEDULE_TASK_FILES_INVALID: {
    errorCode: 10053,
    errorMessage: t(
      "Invalid files: each must reference a valid file with a non-negative weightage, up to {{maxFiles}} files.",
    ),
    isDynamic: true,
    dynamicParams: ["maxFiles"],
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
